import { useState } from "react";
import { Crown, Sparkles, Check, X, Lock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useEntitlements } from "@/context/EntitlementsContext";
import { useAuth } from "@/context/AuthContext";

/**
 * ProCTA — subtle upgrade banner + Manage Plan modal.
 * Reads live plan info from EntitlementsContext (no hardcoded pricing).
 */
export default function ProCTA({ variant = "banner", featureKey = null, testid = "pro-cta" }) {
  const { ent } = useEntitlements();
  const { user } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  if (!ent) return null;
  const planId = (ent.plan && ent.plan.id) || "free";
  const isPaid = planId !== "free";
  const canManage = user?.is_admin;

  // If the caller passes a feature key and it's already unlocked, hide.
  if (featureKey && ent.plan?.feature_flags?.[featureKey]) return null;
  if (isPaid && variant === "banner") {
    return (
      <button
        type="button"
        onClick={() => nav("/profile?tab=subscription")}
        data-testid={testid}
        className="inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-full border border-[var(--border-line)] text-[var(--text-muted)] hover:text-white hover:border-[#FF5A1F]"
      >
        <Crown size={12} className="text-[#FFB86B]" /> Manage plan
      </button>
    );
  }

  if (variant === "banner") {
    return (
      <>
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid={testid}
          className="inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-full border border-[#FF5A1F]/40 text-[#FF5A1F] hover:bg-[#FF5A1F]/10"
        >
          <Sparkles size={12} /> Upgrade to Pro
        </button>
        {open && canManage && <PlansModal onClose={() => setOpen(false)} currentPlanId={planId} />}
        {open && !canManage && <UnlockNoticeModal onClose={() => setOpen(false)} />}
      </>
    );
  }

  // "gate" variant — used to wrap a locked feature
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid={testid}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#FF5A1F] hover:bg-[#E64A0F] text-white text-sm font-semibold"
      >
        <Sparkles size={14} /> View Pro Plans
      </button>
      {open && canManage && <PlansModal onClose={() => setOpen(false)} currentPlanId={planId} />}
      {open && !canManage && <UnlockNoticeModal onClose={() => setOpen(false)} />}
    </>
  );
}

function PlansModal({ onClose, currentPlanId }) {
  const { ent } = useEntitlements();
  const plans = ent?.all_plans && ent.all_plans.length ? ent.all_plans : (ent?.plan ? [ent.plan] : []);
  // Union of every feature_flag key across all plans so each tile shows the same rows.
  const allFeatureKeys = Array.from(new Set(
    plans.flatMap((p) => Object.keys(p.feature_flags || {}))
  )).sort();

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <div
        className="w-full max-w-4xl rounded-2xl border border-[var(--border-line)] bg-[var(--bg-surface)] p-6 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="manage-plan-modal"
      >
        <div className="flex items-start justify-between">
          <div>
            <p className="ft-overline">SUBSCRIPTION</p>
            <h3 className="font-display text-3xl font-bold tracking-tight">Compare plans</h3>
            <p className="mt-1 text-sm text-[var(--text-muted)]">
              Current plan: <span className="text-[#FF5A1F] font-semibold uppercase">{currentPlanId}</span>
            </p>
          </div>
          <button onClick={onClose} className="text-[var(--text-muted)] hover:text-white"><X size={20}/></button>
        </div>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          {plans.length === 0 ? (
            <div className="col-span-3 text-sm text-[var(--text-muted)]">Loading plans…</div>
          ) : plans.map((p) => {
            const isCurrent = p.id === currentPlanId;
            const isRecommended = p.recommended || p.id === "pro";
            return (
              <div
                key={p.id}
                data-testid={`plan-tile-${p.id}`}
                className={
                  "rounded-xl border p-5 relative " +
                  (isCurrent
                    ? "border-[#FF5A1F] bg-[#FF5A1F]/5"
                    : isRecommended
                    ? "border-[#22D3EE]/60 bg-[#22D3EE]/5"
                    : "border-[var(--border-line)]")
                }
              >
                {isRecommended && !isCurrent && (
                  <span className="absolute -top-2.5 left-4 text-[10px] px-2 py-0.5 rounded-full bg-[#22D3EE] text-black uppercase tracking-wider">Recommended</span>
                )}
                <div className="flex items-center justify-between">
                  <h4 className="font-display text-xl font-bold">{p.name || p.id}</h4>
                  {isCurrent && (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#FF5A1F] text-white uppercase">Current</span>
                  )}
                </div>
                {typeof p.price_monthly === "number" && p.price_monthly > 0 ? (
                  <p className="mt-1 text-2xl font-black">
                    ₹{p.price_monthly}<span className="text-xs font-normal text-[var(--text-muted)]">/mo</span>
                    {typeof p.price_annual === "number" && p.price_annual > 0 && (
                      <span className="ml-2 text-sm text-[var(--text-muted)]"> · ₹{p.price_annual}/yr</span>
                    )}
                  </p>
                ) : (
                  <p className="mt-1 text-2xl font-black">Free</p>
                )}

                <ul className="mt-4 space-y-1.5 text-sm">
                  <li className="text-[11px] uppercase tracking-wider text-[var(--text-muted)]">Limits</li>
                  <li className="flex items-center gap-2 text-xs">
                    <Check size={12} className="text-emerald-400"/> {p.vehicle_limit ?? '—'} vehicles
                  </li>
                  <li className="flex items-center gap-2 text-xs">
                    <Check size={12} className="text-emerald-400"/> {p.member_limit ?? '—'} members
                  </li>
                </ul>
                <ul className="mt-3 space-y-1.5 text-sm">
                  <li className="text-[11px] uppercase tracking-wider text-[var(--text-muted)]">Features</li>
                  {allFeatureKeys.map((k) => {
                    const on = !!(p.feature_flags || {})[k];
                    return (
                      <li key={k} className="flex items-center gap-2 text-xs">
                        {on ? (
                          <Check size={12} className="text-emerald-400" />
                        ) : (
                          <X size={12} className="text-[var(--text-muted)]" />
                        )}
                        <span className={on ? "" : "text-[var(--text-muted)]"}>{k.replace(/_/g, " ")}</span>
                      </li>
                    );
                  })}
                </ul>
                {!isCurrent && (
                  <button
                    type="button"
                    onClick={() => { onClose(); window.location.href = `/subscription/checkout?plan_id=${p.id}&billing_cycle=monthly`; }}
                    className="mt-5 inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-[#FF5A1F] hover:bg-[#E64A0F] text-white text-xs font-semibold"
                    data-testid={`select-plan-${p.id}`}
                  >
                    Select {p.name || p.id}
                  </button>
                )}
              </div>
            );
          })}
        </div>

        <p className="mt-6 text-xs text-[var(--text-muted)]">
          Payments go live in the next release. Ask your Super Admin to switch plans in the meantime.
        </p>
      </div>
    </div>
  );
}

function UnlockNoticeModal({ onClose }) {
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-2xl border border-[var(--border-line)] bg-[var(--bg-surface)] p-6" onClick={(e) => e.stopPropagation()} data-testid="unlock-notice-modal">
        <p className="ft-overline">PRO FEATURE</p>
        <h3 className="font-display text-2xl font-bold tracking-tight mt-1">Available on Pro</h3>
        <p className="mt-2 text-sm text-[var(--text-muted)]">
          This feature is included in the Pro plan. Please ask your Account Owner or Admin to upgrade for your team.
        </p>
        <button onClick={onClose} className="mt-5 px-4 py-2 rounded-lg bg-[#FF5A1F] text-white text-sm font-semibold">Got it</button>
      </div>
    </div>
  );
}
